import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { EmailInvoiceButton } from "@/components/EmailInvoiceButton";
import { ReviewRequestButton } from "@/components/ReviewRequestButton";
import { Pencil, FilePlus2, FileText, User, CalendarPlus, Loader2, Menu, Mail, Star } from "lucide-react";

const icon = "w-4 h-4 mr-2";

// Job actions burger menu: quick links + the page's own items (print, delete) passed as children.
export function JobActionsMenu({ legacyId, endUser, contactName, quotes = [], isAdmin, onEdit, onCreateShift, isComplete, children }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const addQuote = async () => {
    setCreating(true);
    try {
      const r = await api.post("/quotes", { task_legacy_id: legacyId, customer_legacy_id: endUser?.legacy_id || null, status: "Draft", lines: [] });
      toast.success(`Created ${r.data.quote_number}`);
      navigate(`/quotes/${r.data.quote_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create quote");
    } finally { setCreating(false); }
  };
  const hasCustomer = endUser?.legacy_id || endUser?.full_name || contactName;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" data-testid="task-actions-menu"><Menu className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isAdmin && <DropdownMenuItem onClick={onEdit} data-testid="job-ql-edit"><Pencil className={icon} /> Edit job</DropdownMenuItem>}
          {can("quotes.manage") && (
            <DropdownMenuItem onClick={addQuote} disabled={creating} data-testid="job-ql-add-quote">
              {creating ? <Loader2 className={`${icon} animate-spin`} /> : <FilePlus2 className={icon} />} Add quote
            </DropdownMenuItem>
          )}
          {can("quotes.view") && quotes.map((q) => (
            <DropdownMenuItem key={q.quote_number} onClick={() => navigate(`/quotes/${q.quote_number}`)} data-testid={`job-ql-quote-${q.quote_number}`}>
              <FileText className={icon} /> View / edit quote {q.quote_number}
            </DropdownMenuItem>
          ))}
          {can("customers.view") && hasCustomer && (
            <DropdownMenuItem data-testid="job-ql-customer"
              onClick={() => navigate(endUser?.legacy_id ? `/users/${endUser.legacy_id}` : `/users?q=${encodeURIComponent(endUser?.full_name || contactName)}`)}>
              <User className={icon} /> View customer
            </DropdownMenuItem>
          )}
          {isAdmin && <DropdownMenuItem onClick={onCreateShift} data-testid="job-ql-create-shift"><CalendarPlus className={icon} /> Create shift</DropdownMenuItem>}
          {(can("finance.manage") || can("quotes.manage")) && (
            <DropdownMenuItem onClick={() => setInvoiceOpen(true)} data-testid="job-ql-email-invoice"><Mail className={icon} /> Email invoice</DropdownMenuItem>
          )}
          {isComplete && can("communications.send") && (
            <DropdownMenuItem onClick={() => setReviewOpen(true)} data-testid="job-ql-request-review"><Star className={icon} /> Request a review</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
      <EmailInvoiceButton legacyId={legacyId} defaultEmail={endUser?.email} open={invoiceOpen} onOpenChange={setInvoiceOpen} />
      {isComplete && <ReviewRequestButton legacyId={legacyId} open={reviewOpen} onOpenChange={setReviewOpen} />}
    </>
  );
}
